<script setup lang="ts">
import type { Notification, NotifType } from "@comp-lib/stores/toasterStore";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faXmark, faExclamation, faCheck, faInfo } from "@fortawesome/pro-solid-svg-icons";

const props = defineProps<{
  notifications: Notification[];
  absolutePosition?: boolean;
}>();

const emit = defineEmits(["@close"]);

function getNotifIcon(type: NotifType) {
  switch (type) {
    case "success": return faCheck;
    case "error": return faXmark;
    case "warning": return faExclamation;
    default: return faInfo;
  }
}
</script>

<template>
  <div class="toaster" :style="{ position: props.absolutePosition ? 'absolute' : 'fixed' }">
    <TransitionGroup name="slide-right">
      <div v-for="notif in props.notifications" :key="notif.id" :class="`notif ${notif.type}`">
        <div class="notif-icon">
          <FontAwesomeIcon :icon="getNotifIcon(notif.type)" />
        </div>
        <div>
          <h4 class="notif-title">{{ notif.title }}</h4>
          <p v-if="notif.bodyText" class="notif-bodytext">{{ notif.bodyText }}</p>
        </div>
        <span class="notif-close">
          <FontAwesomeIcon :icon="faXmark" @click="emit('@close', notif.id)" />
        </span>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped lang="scss">
$themes: (
  success: map.get(v.$success, "500"),
  error: map.get(v.$error, "600"),
  info: map.get(v.$info, "400"),
  warning: map.get(v.$warning, "400")
);

@each $notifType, $color in $themes {
  .#{$notifType}.notif {
    border-color: $color;
    .notif-icon {
      background-color: $color;
    }
  }
}
.toaster {
  top: 30px;
  right: 30px;
  z-index: 100;
  width: 350px;
  max-width: calc(100vw - 30px);
  @media (max-width: map.get(v.$breakpoints, "sm")) {
    top: 15px;
    right: 15px;
  }
}
.notif {
  width: 100%;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  padding: 15px;
  border-radius: map.get(v.$border-radius, "sm");
  box-shadow: map.get(v.$shadow, "lg");
  border-left: 5px solid;
  background-color: white;
  &-icon {
    color: white;
    border-radius: map.get(v.$border-radius, "max");
    align-self: baseline;
    flex: 0 0 18px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 18px;
    font-size: .75rem;
    margin-top: 2px;
    margin-right: 2px;
  }
  &-title {
    font-size: 1rem;
    font-weight: map.get(v.$font-weight, "medium");
  }
  &-bodytext {
    color: map.get(v.$gray, "500");
    font-size: .875rem;
    line-height: 150%;
    margin-top: 5px;
  }
  &-close {
    flex-grow: 1;
    display: inline-flex;
    justify-content: flex-end;
    color: map.get(v.$gray, "300");
    transition: color .2s;
    :hover {
      color: map.get(v.$gray, "400");
    }
  }
}
</style>